/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// allow-any-unicode-comment-file
// DO NOT EDIT -- auto-generated by scripts/sync-agent-host-protocol.ts

// Generated from types/actions.ts — do not edit
// Run `npm run generate` to regenerate.

import { ActionType, type IStateAction, type IRootAgentsChangedAction, type IRootActiveSessionsChangedAction, type IRootTerminalsChangedAction, type ISessionReadyAction, type ISessionCreationFailedAction, type ISessionTurnStartedAction, type ISessionDeltaAction, type ISessionResponsePartAction, type ISessionToolCallStartAction, type ISessionToolCallDeltaAction, type ISessionToolCallReadyAction, type ISessionToolCallConfirmedAction, type ISessionToolCallCompleteAction, type ISessionToolCallResultConfirmedAction, type ISessionToolCallContentChangedAction, type ISessionTurnCompleteAction, type ISessionTurnCancelledAction, type ISessionErrorAction, type ISessionTitleChangedAction, type ISessionUsageAction, type ISessionReasoningAction, type ISessionModelChangedAction, type ISessionServerToolsChangedAction, type ISessionActiveClientChangedAction, type ISessionActiveClientToolsChangedAction, type ISessionPendingMessageSetAction, type ISessionPendingMessageRemovedAction, type ISessionQueuedMessagesReorderedAction, type ISessionInputRequestedAction, type ISessionInputAnswerChangedAction, type ISessionInputCompletedAction, type ISessionCustomizationsChangedAction, type ISessionCustomizationToggledAction, type ISessionTruncatedAction, type ISessionIsReadChangedAction, type ISessionIsDoneChangedAction, type ISessionDiffsChangedAction, type ITerminalDataAction, type ITerminalInputAction, type ITerminalResizedAction, type ITerminalClaimedAction, type ITerminalTitleChangedAction, type ITerminalCwdChangedAction, type ITerminalExitedAction, type ITerminalClearedAction } from './actions.js';


// ─── Root vs Session vs Terminal Action Unions ───────────────────────────────

/** Union of all root-scoped actions. */
export type IRootAction =
	| IRootAgentsChangedAction
	| IRootActiveSessionsChangedAction
	| IRootTerminalsChangedAction
	;

/** Union of all session-scoped actions. */
export type ISessionAction =
	| ISessionReadyAction
	| ISessionCreationFailedAction
	| ISessionTurnStartedAction
	| ISessionDeltaAction
	| ISessionResponsePartAction
	| ISessionToolCallStartAction
	| ISessionToolCallDeltaAction
	| ISessionToolCallReadyAction
	| ISessionToolCallConfirmedAction
	| ISessionToolCallCompleteAction
	| ISessionToolCallResultConfirmedAction
	| ISessionToolCallContentChangedAction
	| ISessionTurnCompleteAction
	| ISessionTurnCancelledAction
	| ISessionErrorAction
	| ISessionTitleChangedAction
	| ISessionUsageAction
	| ISessionReasoningAction
	| ISessionModelChangedAction
	| ISessionServerToolsChangedAction
	| ISessionActiveClientChangedAction
	| ISessionActiveClientToolsChangedAction
	| ISessionPendingMessageSetAction
	| ISessionPendingMessageRemovedAction
	| ISessionQueuedMessagesReorderedAction
	| ISessionInputRequestedAction
	| ISessionInputAnswerChangedAction
	| ISessionInputCompletedAction
	| ISessionCustomizationsChangedAction
	| ISessionCustomizationToggledAction
	| ISessionTruncatedAction
	| ISessionIsReadChangedAction
	| ISessionIsDoneChangedAction
	| ISessionDiffsChangedAction
	;

/** Union of session actions that clients may dispatch. */
export type IClientSessionAction =
	| ISessionTurnStartedAction
	| ISessionToolCallConfirmedAction
	| ISessionToolCallCompleteAction
	| ISessionToolCallResultConfirmedAction
	| ISessionTurnCancelledAction
	| ISessionTitleChangedAction
	| ISessionModelChangedAction
	| ISessionActiveClientChangedAction
	| ISessionActiveClientToolsChangedAction
	| ISessionPendingMessageSetAction
	| ISessionPendingMessageRemovedAction
	| ISessionQueuedMessagesReorderedAction
	| ISessionInputAnswerChangedAction
	| ISessionInputCompletedAction
	| ISessionCustomizationToggledAction
	| ISessionTruncatedAction
	| ISessionIsReadChangedAction
	| ISessionIsDoneChangedAction
	;

/** Union of session actions that only the server may produce. */
export type IServerSessionAction =
	| ISessionReadyAction
	| ISessionCreationFailedAction
	| ISessionDeltaAction
	| ISessionResponsePartAction
	| ISessionToolCallStartAction
	| ISessionToolCallDeltaAction
	| ISessionToolCallReadyAction
	| ISessionToolCallContentChangedAction
	| ISessionTurnCompleteAction
	| ISessionErrorAction
	| ISessionUsageAction
	| ISessionReasoningAction
	| ISessionServerToolsChangedAction
	| ISessionInputRequestedAction
	| ISessionCustomizationsChangedAction
	| ISessionDiffsChangedAction
	;

/** Union of all terminal-scoped actions. */
export type ITerminalAction =
	| ITerminalDataAction
	| ITerminalInputAction
	| ITerminalResizedAction
	| ITerminalClaimedAction
	| ITerminalTitleChangedAction
	| ITerminalCwdChangedAction
	| ITerminalExitedAction
	| ITerminalClearedAction
	;

/** Union of terminal actions that clients may dispatch. */
export type IClientTerminalAction =
	| ITerminalInputAction
	| ITerminalResizedAction
	| ITerminalClaimedAction
	| ITerminalTitleChangedAction
	| ITerminalClearedAction
	;

/** Union of terminal actions that only the server may produce. */
export type IServerTerminalAction =
	| ITerminalDataAction
	| ITerminalCwdChangedAction
	| ITerminalExitedAction
	;

// ─── Client-Dispatchable Map ─────────────────────────────────────────────────

/**
 * Exhaustive map indicating which action types may be dispatched by clients.
 * Adding a new action to IStateAction without adding it here is a compile error.
 */
export const IS_CLIENT_DISPATCHABLE: { readonly [K in IStateAction['type']]: boolean } = {
	[ActionType.RootAgentsChanged]: false,
	[ActionType.RootActiveSessionsChanged]: false,
	[ActionType.RootTerminalsChanged]: false,
	[ActionType.SessionReady]: false,
	[ActionType.SessionCreationFailed]: false,
	[ActionType.SessionTurnStarted]: true,
	[ActionType.SessionDelta]: false,
	[ActionType.SessionResponsePart]: false,
	[ActionType.SessionToolCallStart]: false,
	[ActionType.SessionToolCallDelta]: false,
	[ActionType.SessionToolCallReady]: false,
	[ActionType.SessionToolCallConfirmed]: true,
	[ActionType.SessionToolCallComplete]: true,
	[ActionType.SessionToolCallResultConfirmed]: true,
	[ActionType.SessionToolCallContentChanged]: false,
	[ActionType.SessionTurnComplete]: false,
	[ActionType.SessionTurnCancelled]: true,
	[ActionType.SessionError]: false,
	[ActionType.SessionTitleChanged]: true,
	[ActionType.SessionUsage]: false,
	[ActionType.SessionReasoning]: false,
	[ActionType.SessionModelChanged]: true,
	[ActionType.SessionServerToolsChanged]: false,
	[ActionType.SessionActiveClientChanged]: true,
	[ActionType.SessionActiveClientToolsChanged]: true,
	[ActionType.SessionPendingMessageSet]: true,
	[ActionType.SessionPendingMessageRemoved]: true,
	[ActionType.SessionQueuedMessagesReordered]: true,
	[ActionType.SessionInputRequested]: false,
	[ActionType.SessionInputAnswerChanged]: true,
	[ActionType.SessionInputCompleted]: true,
	[ActionType.SessionCustomizationsChanged]: false,
	[ActionType.SessionCustomizationToggled]: true,
	[ActionType.SessionTruncated]: true,
	[ActionType.SessionIsReadChanged]: true,
	[ActionType.SessionIsDoneChanged]: true,
	[ActionType.SessionDiffsChanged]: false,
	[ActionType.TerminalData]: false,
	[ActionType.TerminalInput]: true,
	[ActionType.TerminalResized]: true,
	[ActionType.TerminalClaimed]: true,
	[ActionType.TerminalTitleChanged]: true,
	[ActionType.TerminalCwdChanged]: false,
	[ActionType.TerminalExited]: false,
	[ActionType.TerminalCleared]: true,
};
